import React, {
  useEffect,
  useState,
  useRef,
  PropsWithChildren,
  useCallback,
  useMemo,
} from 'react';

import { useRouter } from 'next/router';
import { LeafletContext, LeafletProvider } from 'react-leaflet';
import { LatLngBounds as LeafletLatLngBounds, ZoomPanOptions } from 'leaflet';

import { LatLngBounds } from '@typings/geo';

import {
  MapContext,
  defaultCenter,
  defaultZoom,
  maxZoom,
  minZoom,
  maxBounds,
  MapContextType,
} from './map-context';
import { parseQueryBounds } from './map-context.utils';
import { useMapDataEvents } from './map-context.hooks';
import { MapViewState } from './map-context.types';

export type MapContextProviderProps = PropsWithChildren<{
  initialBounds?: LatLngBounds;
  overrideBounds?: LatLngBounds;
  onBoundsChange?: (bounds: LatLngBounds) => void;
  preprocessDefaultBounds?: (
    bounds: LatLngBounds,
    isInitial: boolean,
  ) => LatLngBounds;
}>;

export const MapContextProvider = ({
  children,
  initialBounds,
  overrideBounds,
  onBoundsChange,
  preprocessDefaultBounds,
}: MapContextProviderProps) => {
  const router = useRouter();

  const leafletContextRef = useRef<LeafletContext>({});

  const [satellite, setSatellite] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [initialized, setInitialized] = useState(false);

  /**
   * map view state
   * - getting latest bounds, center, zoom from map
   * - providing to whole app
   * - default value is taken from URL or initialBounds prop
   */
  const [viewState, setViewState] = useState(() => {
    const state: MapViewState = {
      center: defaultCenter,
      zoom: defaultZoom,
      bounds: maxBounds,
    };

    const parsedSearch =
      router.query.search && JSON.parse(router.query.search as string);

    let isInitial = false;
    if (
      router.query &&
      !router.query.bounds &&
      parsedSearch &&
      parsedSearch.point
    ) {
      state.center = parsedSearch.point;
      state.zoom = 18;
    } else if (overrideBounds) {
      state.bounds = overrideBounds;
    } else if (router.query && router.query.bounds) {
      state.bounds = parseQueryBounds(router.query.bounds as string);
    } else if (initialBounds) {
      state.bounds = initialBounds;
      isInitial = true;
    }

    if (preprocessDefaultBounds)
      state.bounds = preprocessDefaultBounds(state.bounds, isInitial);

    return state;
  });

  // updates URL when map view changes

  useEffect(() => {
    if (!initialized) return;
    if (onBoundsChange) onBoundsChange(viewState.bounds);
  }, [viewState.bounds, initialized, onBoundsChange]);

  // updating position state
  const sizeInvalidatedRef = useRef(false);
  const positionChangeInterval = useRef<NodeJS.Timeout>();
  const positionChangeDebounced = useCallback(() => {
    if (
      sizeInvalidatedRef.current === true &&
      leafletContextRef.current !== undefined &&
      leafletContextRef.current.map !== undefined
    ) {
      leafletContextRef.current.map.invalidateSize();
      const actualBounds = leafletContextRef.current.map.getBounds();
      const actualCenter = leafletContextRef.current.map.getCenter();
      const actualZoom = leafletContextRef.current.map.getZoom();

      const southWest = actualBounds.getSouthWest();
      const northEast = actualBounds.getNorthEast();

      const newViewState: MapViewState = {
        bounds: [
          [southWest.lat, southWest.lng],
          [northEast.lat, northEast.lng],
        ],
        center: [actualCenter.lat, actualCenter.lng],
        zoom: actualZoom,
      };

      const stateBounds = new LeafletLatLngBounds(viewState.bounds);

      if (
        !actualBounds.equals(stateBounds) ||
        viewState.center[0] !== actualCenter.lat ||
        viewState.center[1] !== actualCenter.lng ||
        viewState.zoom !== actualZoom
      ) {
        setViewState(newViewState);
      }
    }
    positionChangeInterval.current = undefined;
  }, [viewState]);
  const positionChange = useCallback(() => {
    if (positionChangeInterval.current === undefined) {
      positionChangeInterval.current = setTimeout(positionChangeDebounced, 200);
    }
  }, []);

  // Map initialization
  const initializationInterval = useRef<NodeJS.Timeout>();
  const initializationCallback = useCallback(() => {
    if (
      !(
        initialized === true &&
        leafletContextRef.current !== undefined &&
        leafletContextRef.current.map !== undefined
      )
    ) {
      return;
    }

    // Fix: map is not loading complete without whit
    leafletContextRef.current.map.invalidateSize({ animate: false });

    // Set default view
    leafletContextRef.current.map.fitBounds(viewState.bounds, {
      animate: false,
    });

    // Set controls position
    leafletContextRef.current.map.zoomControl.setPosition('bottomleft');

    // Start listening for moveend and dragend
    leafletContextRef.current.map.on('moveend', positionChange);
    leafletContextRef.current.map.on('dragend', positionChange);

    // link to the pointer might be shared from bigger screen so we need to make sure it wont be clustered
    if (router.query !== undefined && router.query.pointer_id !== undefined) {
      leafletContextRef.current.map.setZoom(17);
    }

    sizeInvalidatedRef.current = true;
    positionChangeDebounced();
    setLoaded(true);
  }, [initialized, router.query, viewState]);

  //
  useEffect(() => {
    if (
      initialized === true &&
      leafletContextRef.current !== undefined &&
      leafletContextRef.current.map !== undefined
    ) {
      leafletContextRef.current.map.invalidateSize({ animate: false }); // Fix: map is not loading complete without it
      initializationInterval.current = setTimeout(initializationCallback, 200);
    }
    return () => {
      if (initializationInterval.current !== undefined) {
        clearTimeout(initializationInterval.current);
      }
      if (positionChangeInterval.current !== undefined) {
        clearTimeout(positionChangeInterval.current);
      }
    };
  }, [initialized]);

  // Zoom
  const zoomInDisabled = useMemo(
    () => viewState.zoom >= maxZoom,
    [viewState.zoom],
  );
  const zoomOutDisabled = useMemo(
    () => viewState.zoom <= minZoom,
    [viewState.zoom],
  );

  const contextValue: MapContextType = {
    initialize: (leafletContext: LeafletContext) => {
      leafletContextRef.current = leafletContext;

      if (initialized === false) {
        if (leafletContext.map) {
          leafletContext.map.addEventListener('load', () => {
            if (leafletContext.map && defaultCenter !== viewState.center) {
              leafletContext.map.setView(viewState.center, viewState.zoom);
            }
          });
        }

        setInitialized(true);
      }
    },
    initialized,
    loaded,
    center: viewState.center,
    satellite,
    setSatellite,
    zoom: viewState.zoom,
    zoomInDisabled,
    zoomOutDisabled,
    zoomIn: () =>
      leafletContextRef.current !== undefined &&
      leafletContextRef.current.map !== undefined
        ? leafletContextRef.current.map.zoomIn()
        : {},
    zoomOut: () =>
      leafletContextRef.current !== undefined &&
      leafletContextRef.current.map !== undefined
        ? leafletContextRef.current.map.zoomOut()
        : {},
    zoomTo: (position, amount, options?: ZoomPanOptions) => {
      if (
        leafletContextRef.current !== undefined &&
        leafletContextRef.current.map !== undefined
      ) {
        leafletContextRef.current.map.setView(position, amount, options);
      }
    },
    setBounds: (bounds, opts) => {
      if (
        leafletContextRef.current !== undefined &&
        leafletContextRef.current.map !== undefined
      ) {
        leafletContextRef.current.map.fitBounds(bounds, opts);
      }
    },
    bounds: viewState.bounds,
    on: (event, callback) => {
      if (
        leafletContextRef.current !== undefined &&
        leafletContextRef.current.map !== undefined
      ) {
        leafletContextRef.current.map.on(event, callback);
        return () => {
          leafletContextRef.current.map &&
            leafletContextRef.current.map.off(event, callback);
        };
      }
    },
    once: (event, callback) => {
      if (
        leafletContextRef.current !== undefined &&
        leafletContextRef.current.map !== undefined
      ) {
        leafletContextRef.current.map.once(event, callback);
      }
    },
  };

  useMapDataEvents(contextValue);

  return (
    <MapContext.Provider value={contextValue}>
      <LeafletProvider value={leafletContextRef.current}>
        {children}
      </LeafletProvider>
    </MapContext.Provider>
  );
};
